import { Box } from '@factorialco/deprecated-design-system'
import CheckCircleLine from '@factorialco/f0-react/icons/app/CheckCircleLine'
import ClockBack from '@factorialco/f0-react/icons/app/ClockBack'
import Spinner from '@factorialco/f0-react/icons/app/Spinner'
import type {
  AvailablePayrollConceptFieldFragment,
  IntegrationsSyncableStateFieldsFragment,
  PayrollRunEmployeesCompensationFormFragment,
} from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useSyncStateTranslations } from 'modules/integrations/hooks/useSyncStateTranslations'

import { FailedLabel } from './FailedLabel'
import { SimpleLabel } from './SimpleLabel'
import { WarningLabel } from './WarningLabel'

const formatDate = (date: string) => {
  return i18nDateToString(stringToDate(date, 'backend_datetime'), 'ui_month_day_time')
}

const resolveSyncableState = (
  syncableStateProp: IntegrationsSyncableStateFieldsFragment | null | undefined,
  compensations?: Array<PayrollRunEmployeesCompensationFormFragment>,
  concept?: AvailablePayrollConceptFieldFragment
): IntegrationsSyncableStateFieldsFragment | null => {
  if (syncableStateProp !== undefined) {
    return syncableStateProp
  }

  if (!compensations || !concept) {
    return null
  }

  const compensation = compensations.find((node) => node?.payrollConcept?.id === concept.id)

  return compensation?.syncableState?.[0] ?? null
}

interface SyncStateLabelProps {
  // Compensations
  payrollRunEmployeeCompensations?: Array<PayrollRunEmployeesCompensationFormFragment>
  compensationConcept?: AvailablePayrollConceptFieldFragment
  // Expenses
  syncableState?: IntegrationsSyncableStateFieldsFragment | null
  // Layout control
  layout?: 'wrap' | 'noWrap'
}

export const SyncStateLabel = ({
  payrollRunEmployeeCompensations,
  compensationConcept,
  syncableState: syncableStateProp,
  layout = 'wrap',
}: SyncStateLabelProps) => {
  const { syncEnabled } = usePrivateIntegrationsSyncContext()
  const translations = useSyncStateTranslations()

  const syncableState = resolveSyncableState(
    syncableStateProp,
    payrollRunEmployeeCompensations,
    compensationConcept
  )

  if (!syncEnabled) return null

  const status = syncableState?.status

  const statusContentMap: Record<string, JSX.Element> = {
    failed: <FailedLabel errorMessages={syncableState?.errorMessages ?? []} />,
    invalid: <FailedLabel errorMessages={syncableState?.errorMessages ?? []} />,
    syncing: <SimpleLabel label={translations.syncing} icon={Spinner} />,
    outdated: <SimpleLabel label={translations.outdated} icon={ClockBack} />,
    synced: (
      <SimpleLabel
        label={`${translations.synced} ${formatDate(syncableState?.statusUpdatedAt)}`}
        icon={CheckCircleLine}
      />
    ),
  }

  const content = (status && statusContentMap[status]) || (
    <WarningLabel label={translations.pending} />
  )

  if (layout === 'noWrap') {
    return content
  }

  return (
    <Box paddingTop="s8" paddingX="s12" width="full" textAlign="right">
      {content}
    </Box>
  )
}
